// ==============================|| REACT & THIRD-PARTY IMPORTS ||============================== //
import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Button,
    Typography,
    Stack,
    Divider,
    Box,
    CircularProgress,
    useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DeleteFilled } from '@ant-design/icons';

// ==============================|| LOCAL COMPONENTS & ICONS ||============================== //
import { MixedAssetsIcon } from 'components/CommonFileManager/CommonFilemanagerIcon';

// ==============================|| TYPES & INTERFACES ||============================== //
import { DeleteTaskModalProps } from 'types/taskManagement/TaskManagement';

export default function DeleteTaskModal({
    open,
    onClose,
    onConfirm,
    taskTitle = '',
    filesCount = 0,
    loading: externalLoading
}: DeleteTaskModalProps) {
    const theme = useTheme();
    const [internalLoading, setInternalLoading] = useState(false);
    const isLoading = Boolean(externalLoading || internalLoading);

    const handleConfirm = async () => {
        if (!onConfirm || isLoading) return;
        try {
            setInternalLoading(true);
            await onConfirm();
        } catch (error) {
            console.error('Error deleting task:', error);
        } finally {
            setInternalLoading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (!isLoading && reason !== 'backdropClick' && reason !== 'escapeKeyDown') {
                    onClose();
                }
            }}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: {
                    width: '550px',
                    borderRadius: '8px',
                    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.08)',
                    p: 3,
                    overflow: 'hidden'
                }
            }}
        >
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                {/* Round Red Icon Container */}
                <Box
                    sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        backgroundColor: alpha(theme.palette.error.main, 0.08),
                        color: theme.palette.error.main,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 3.5
                    }}
                >
                    <DeleteFilled style={{ fontSize: '32px' }} />
                </Box>

                {/* Dialog Title */}
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        mb: 1.5,
                        fontSize: '20px'
                    }}
                >
                    Delete Task?
                </Typography>

                {/* Question & Detail Message */}
                <Typography
                    variant="subtitle2"
                    fontWeight={400}
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '13.5px',
                        lineHeight: 1.6,
                        mb: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        maxWidth: '100%'
                    }}
                >
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>Are you sure you want to delete</Box>
                    <Box
                        component="span"
                        title={taskTitle}
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            display: 'inline-block',
                            maxWidth: '260px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom'
                        }}
                    >
                        “{taskTitle}”
                    </Box>
                    <Box component="span" sx={{ whiteSpace: 'nowrap' }}>task?</Box>
                </Typography>

                <Typography
                    variant="subtitle2"
                    fontWeight={400}
                    sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '13.5px',
                        lineHeight: 1.6,
                        mb: 3.5,
                        maxWidth: 420
                    }}
                >
                    This action permanently deletes the task and all related data.
                    It cannot be undone.
                </Typography>

                {/* Assets Card Block */}
                {filesCount > 0 && (
                    <Box
                        sx={{
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.grey[100],
                            borderRadius: '12px',
                            p: 1.5,
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            textAlign: 'left',
                            mb: 3
                        }}
                    >
                        <Box
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: '8px',
                                width: 45,
                                height: 45,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                                color: theme.palette.text.secondary,
                                flexShrink: 0
                            }}
                        >
                            <MixedAssetsIcon />
                        </Box>

                        <Stack spacing={0.3} sx={{ overflow: 'hidden' }}>
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 600,
                                    color: theme.palette.text.primary,
                                    fontSize: '15px',
                                    lineHeight: 1.5,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                Assets
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 400,
                                    color: theme.palette.error.main,
                                    fontSize: '13px',
                                    lineHeight: 1.2
                                }}
                            >
                                {filesCount} file{filesCount > 1 ? 's' : ''} will be deleted
                            </Typography>
                        </Stack>
                    </Box>
                )}

                <Divider sx={{ width: '100%', mb: 3 }} />

                {/* Action Buttons */}
                <Stack direction="row" spacing={2} sx={{ width: '100%', justifyContent: 'right' }}>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={onClose}
                        disabled={isLoading}
                        color="secondary"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            borderRadius: '6px',
                            px: 3,
                            py: 1,
                            minWidth: 130,
                            borderColor: theme.palette.grey[400],
                            color: theme.palette.text.primary,
                            '&:hover': {
                                borderColor: theme.palette.grey[600],
                                backgroundColor: alpha(theme.palette.action.hover, 0.04)
                            }
                        }}
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleConfirm}
                        disabled={isLoading}
                        startIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : null}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 500,
                            fontSize: '15px',
                            borderRadius: '6px',
                            px: 3,
                            py: 1.2,
                            minWidth: 160,
                            bgcolor: 'error.main',
                            '&:hover': { bgcolor: 'error.dark' },
                            boxShadow: 'none'
                        }}
                    >
                        {isLoading ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </Stack>
            </DialogContent>
        </Dialog>
    );
}